// Search Intelligence (/search) -- legacy Tavily tools for agency/admin
// operators. Brand founders are routed to Web Search Visibility instead
// (App.js PersonaGuard + the backend persona check on /search/geo-check).
//
// The GEO tab spends Tavily searches, so it has the same safeguards as
// /geo-report: it is bound to one owned workspace (checked against that
// workspace's own website), a run never starts from the first click, the
// shared GeoRunConfirmDialog shows the backend's read-only estimate for the
// exact prompts, Cancel sends nothing, and one confirmation sends at most
// one request.
import { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { GeoRunConfirmDialog } from '../components/GeoRunConfirmDialog';
import { WorkspaceSelect } from '../components/brand/BrandContextBar';
import { useWorkspaceSelection } from '../hooks/useWorkspaceSelection';
import { searchAPI, createIdempotencyKey } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  MagnifyingGlass, Globe, Lightning, CircleNotch, ArrowSquareOut, Check, X,
  Binoculars, Target, ChartLine
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function SearchPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const [compDomain, setCompDomain] = useState('');
  const [compKeywords, setCompKeywords] = useState('');
  const [compResults, setCompResults] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const { clients, workspaceId, setWorkspaceId, loadingClients } = useWorkspaceSelection();
  const geoWorkspace = clients.find((c) => c.id === workspaceId) || null;
  const [geoPrompts, setGeoPrompts] = useState('');
  const [geoResults, setGeoResults] = useState(null);
  const [geoChecking, setGeoChecking] = useState(false);
  // { scope: 'workspace', clientId, prompts, estimate, loading, error }
  const [geoConfirm, setGeoConfirm] = useState(null);
  const geoInFlight = useRef(false); // at most one paid request per confirmation

  const [tavilyStatus, setTavilyStatus] = useState(null);
  const [usage, setUsage] = useState(null);

  const loadStatus = () => {
    searchAPI.status().then(r => setTavilyStatus(r.data)).catch(() => {});
    searchAPI.usage().then(r => setUsage(r.data)).catch(() => {});
  };

  useEffect(() => { loadStatus(); }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await searchAPI.web(searchQuery);
      setSearchResults(res.data);
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleCompetitorAnalysis = async () => {
    if (!compDomain.trim()) return;
    setAnalyzing(true);
    try {
      const keywords = compKeywords.split(',').map(k => k.trim()).filter(Boolean);
      const res = await searchAPI.competitor(compDomain, keywords);
      setCompResults(res.data);
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  // Opening the confirmation only reads the backend's estimate -- no search.
  const openGeoConfirm = async () => {
    if (!workspaceId || geoInFlight.current) return;
    const clientId = workspaceId;
    const prompts = geoPrompts.split('\n').map(p => p.trim()).filter(Boolean);
    setGeoConfirm({ scope: 'workspace', clientId, prompts, idempotencyKey: createIdempotencyKey(), estimate: null, loading: true, error: null });
    try {
      const r = await searchAPI.geoCheckEstimate(clientId, prompts);
      setGeoConfirm((c) => (c && c.clientId === clientId && c.prompts === prompts ? { ...c, estimate: r.data, loading: false } : c));
    } catch (err) {
      setGeoConfirm((c) => (c && c.clientId === clientId && c.prompts === prompts ? { ...c, loading: false, error: err.response?.data?.detail || 'unavailable' } : c));
    }
  };

  const cancelGeoConfirm = () => { if (!geoInFlight.current) setGeoConfirm(null); };

  // Runs exactly the workspace and prompts that were estimated and confirmed.
  const runGeoConfirmed = async () => {
    if (!geoConfirm || geoInFlight.current) return;
    geoInFlight.current = true;
    setGeoChecking(true);
    try {
      const res = await searchAPI.geoCheck(geoConfirm.clientId, geoConfirm.prompts, geoConfirm.idempotencyKey);
      setGeoResults(res.data);
      setGeoConfirm(null);
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "The check couldn't be run.");
    } finally {
      geoInFlight.current = false;
      setGeoChecking(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="search-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight">
              Search Intelligence
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              Live web search, competitor analysis & GEO visibility — powered by Tavily AI
            </p>
          </div>
          {tavilyStatus && (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded border ${
              tavilyStatus.tavily_configured
                ? 'bg-[var(--success-bg)] border-[var(--success-border)]'
                : 'bg-[var(--error-bg)] border-[var(--error-border)]'
            }`} data-testid="tavily-status">
              <span className={`nx-status-dot ${tavilyStatus.tavily_configured ? 'active' : 'paused'}`} />
              <span className={`text-xs font-mono ${tavilyStatus.tavily_configured ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
                {tavilyStatus.tavily_configured ? 'Tavily Connected' : 'Tavily Not Configured'}
              </span>
            </div>
          )}
        </div>

        {/* Credit Usage Widget */}
        {usage && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="credit-usage-widget">
            <div className="nx-metric-card rounded-lg">
              <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Credits Used</p>
              <p className="text-2xl font-bold text-[var(--text-primary)] font-mono">{usage.current_month?.credits_used || 0}</p>
              <div className="w-full h-1.5 bg-[var(--neutral-bg)] rounded-full mt-2 overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(usage.usage_percent || 0, 100)}%`,
                    background: (usage.usage_percent || 0) > 80 ? 'var(--error)' : (usage.usage_percent || 0) > 50 ? 'var(--warning)' : 'var(--accent)'
                  }} />
              </div>
            </div>
            <div className="nx-metric-card rounded-lg">
              <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Remaining</p>
              <p className={`text-2xl font-bold font-mono ${usage.credits_remaining > 200 ? 'text-[var(--success)]' : usage.credits_remaining > 50 ? 'text-[var(--warning)]' : 'text-[var(--error)]'}`}>
                {usage.credits_remaining}
              </p>
            </div>
            <div className="nx-metric-card rounded-lg">
              <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">API Calls</p>
              <p className="text-2xl font-bold text-[var(--accent)] font-mono">{usage.current_month?.calls || 0}</p>
            </div>
            <div className="nx-metric-card rounded-lg">
              <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Monthly Limit</p>
              <p className="text-2xl font-bold text-[var(--text-primary)] font-mono">1,000</p>
              <p className="text-[10px] text-[var(--text-tertiary)] font-mono mt-1">{usage.usage_percent || 0}% used</p>
            </div>
          </div>
        )}

        <Tabs defaultValue="search" className="space-y-4">
          <TabsList className="bg-[var(--surface)] border border-[var(--border)] max-w-full h-auto flex-wrap justify-start">
            <TabsTrigger value="search" data-testid="tab-web-search" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">
              <MagnifyingGlass size={14} className="mr-1.5" /> Web Search
            </TabsTrigger>
            <TabsTrigger value="competitor" data-testid="tab-competitor" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">
              <Binoculars size={14} className="mr-1.5" /> Competitor Analysis
            </TabsTrigger>
            <TabsTrigger value="geo" data-testid="tab-geo-check" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">
              <Target size={14} className="mr-1.5" /> GEO Visibility Check
            </TabsTrigger>
          </TabsList>

          {/* ═══ WEB SEARCH ═══ */}
          <TabsContent value="search" className="space-y-4">
            <div className="nx-metric-card rounded-lg" data-testid="web-search-panel">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                <Lightning size={18} className="inline mr-2 text-[var(--accent)]" weight="fill" />
                AI-Powered Web Search
              </h3>
              <div className="flex gap-3">
                <Input data-testid="search-query-input" value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search anything — SEO trends, competitor strategies, industry data..."
                  className="flex-1 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                  onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                />
                <Button data-testid="search-submit-btn" onClick={handleSearch} disabled={searching || !searchQuery.trim()}
                  className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white px-6 disabled:opacity-50">
                  {searching ? <CircleNotch size={16} className="animate-spin" /> : <MagnifyingGlass size={16} />}
                  <span className="ml-2">Search</span>
                </Button>
              </div>
            </div>

            {searchResults && (
              <div className="space-y-4 animate-fade-in-up">
                {searchResults.answer && (
                  <div className="nx-metric-card rounded-lg border-[var(--accent-border)]" data-testid="search-answer">
                    <div className="flex items-center gap-2 mb-3">
                      <Lightning size={16} className="text-[var(--accent)]" weight="fill" />
                      <span className="text-xs text-[var(--accent)] font-mono uppercase tracking-wider">AI Answer</span>
                    </div>
                    <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{searchResults.answer}</p>
                  </div>
                )}
                <div className="nx-metric-card rounded-lg" data-testid="search-results-list">
                  <h4 className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-3">
                    {searchResults.result_count} Results for "{searchResults.query}"
                  </h4>
                  <div className="space-y-3">
                    {searchResults.results?.map((r, i) => (
                      <div key={i} className="p-3 rounded border border-[var(--border-light)] hover:border-[var(--border)] transition-border-color duration-200">
                        <a href={r.url} target="_blank" rel="noopener noreferrer"
                          className="text-sm text-[var(--accent)] hover:text-[var(--accent-hover)] font-medium flex items-center gap-1.5 transition-colors duration-200">
                          {r.title} <ArrowSquareOut size={12} />
                        </a>
                        <p className="text-xs text-[var(--text-tertiary)] font-mono mt-0.5 truncate">{r.url}</p>
                        <p className="text-xs text-[var(--text-secondary)] mt-1.5 leading-relaxed line-clamp-3">{r.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ═══ COMPETITOR ANALYSIS ═══ */}
          <TabsContent value="competitor" className="space-y-4">
            <div className="nx-metric-card rounded-lg" data-testid="competitor-panel">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                <Binoculars size={18} className="inline mr-2 text-[var(--accent)]" weight="duotone" />
                Competitor Domain Analysis
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Competitor Domain</label>
                  <Input data-testid="competitor-domain-input" value={compDomain}
                    onChange={e => setCompDomain(e.target.value)}
                    placeholder="competitor.com" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" />
                </div>
                <div>
                  <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Target Keywords (comma-separated)</label>
                  <Input data-testid="competitor-keywords-input" value={compKeywords}
                    onChange={e => setCompKeywords(e.target.value)}
                    placeholder="seo tools, keyword research, rank tracking"
                    className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" />
                </div>
                <Button data-testid="competitor-analyze-btn" onClick={handleCompetitorAnalysis}
                  disabled={analyzing || !compDomain.trim()}
                  className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white disabled:opacity-50">
                  {analyzing ? (
                    <span className="flex items-center gap-2"><CircleNotch size={16} className="animate-spin" /> Analyzing...</span>
                  ) : (
                    <span className="flex items-center gap-2"><Binoculars size={16} /> Analyze Competitor</span>
                  )}
                </Button>
              </div>
            </div>

            {compResults && (
              <div className="space-y-4 animate-fade-in-up">
                {/* Score */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="nx-metric-card rounded-lg" data-testid="comp-score">
                    <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Visibility Score</p>
                    <p className={`text-4xl font-bold font-mono ${
                      compResults.visibility_score >= 60 ? 'text-[var(--success)]' : compResults.visibility_score >= 30 ? 'text-[var(--warning)]' : 'text-[var(--error)]'
                    }`}>{compResults.visibility_score}%</p>
                  </div>
                  <div className="nx-metric-card rounded-lg">
                    <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Domain</p>
                    <p className="font-display text-lg text-[var(--text-primary)] font-bold">{compResults.domain}</p>
                  </div>
                  <div className="nx-metric-card rounded-lg">
                    <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Keywords Visible</p>
                    <p className="text-lg text-[var(--text-primary)] font-mono">{compResults.keyword_visibility}</p>
                  </div>
                </div>

                {/* Analysis results */}
                {compResults.analyses?.map((a, i) => (
                  <div key={i} className="nx-metric-card rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`nx-tag ${a.type === 'overview' ? 'nx-tag-blue' : 'nx-tag-green'}`}>{a.type}</span>
                      {a.keyword && <span className="text-sm text-[var(--text-primary)] font-medium">{a.keyword}</span>}
                      {a.domain_visible !== undefined && (
                        <span className={`flex items-center gap-1 text-xs ${a.domain_visible ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
                          {a.domain_visible ? <Check size={12} weight="bold" /> : <X size={12} weight="bold" />}
                          {a.domain_visible ? `Visible (pos #${a.position_estimate || '?'})` : 'Not visible'}
                        </span>
                      )}
                    </div>
                    {a.answer && <p className="text-xs text-[var(--text-secondary)] leading-relaxed mb-2">{a.answer}</p>}
                    {a.sources?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {a.sources.slice(0, 3).map((s, j) => (
                          <a key={j} href={s.url} target="_blank" rel="noopener noreferrer"
                            className="text-[10px] text-[var(--text-tertiary)] font-mono hover:text-[var(--accent)] truncate max-w-[200px] transition-colors duration-200">
                            {s.title || s.url}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ═══ GEO VISIBILITY CHECK ═══ */}
          <TabsContent value="geo" className="space-y-4">
            <div className="nx-metric-card rounded-lg" data-testid="geo-check-panel">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                <Target size={18} className="inline mr-2 text-[var(--accent)]" weight="duotone" />
                Generative Engine Optimization (GEO) Check
              </h3>
              <p className="text-xs text-[var(--text-secondary)] mb-4 leading-relaxed">
                Runs live Tavily web searches about one workspace's website and records whether the site is mentioned in Tavily's answer or linked in the results. This is a web search signal, not AI-assistant answers.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Workspace</label>
                  {!loadingClients && clients.length === 0 ? (
                    <p className="text-xs text-[var(--text-secondary)]" data-testid="geo-no-workspace">Add a workspace with a website to run a check.</p>
                  ) : (
                    <WorkspaceSelect clients={clients} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} />
                  )}
                  {geoWorkspace && (
                    <p className="mt-1.5 text-xs text-[var(--text-secondary)]" data-testid="geo-workspace-website">
                      {geoWorkspace.website ? `Checks ${geoWorkspace.website} — this workspace's own website.` : 'This workspace has no website, so it cannot be checked yet.'}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Custom Prompts (one per line, optional — up to 6; blank lines are ignored)</label>
                  <textarea data-testid="geo-prompts-input" value={geoPrompts}
                    onChange={e => setGeoPrompts(e.target.value)}
                    placeholder={"What is yoursite.com?\nBest alternatives to yoursite.com\nReviews of yoursite.com"}
                    rows={3}
                    className="w-full bg-[var(--surface-sunken)] border border-[var(--border)] text-[var(--text-primary)] rounded-md px-3 py-2 text-sm placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)] focus:outline-none resize-none"
                  />
                </div>
                <Button data-testid="geo-check-btn" onClick={openGeoConfirm}
                  disabled={geoChecking || !workspaceId || !!geoConfirm}
                  className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white disabled:opacity-50">
                  {geoChecking ? (
                    <span className="flex items-center gap-2"><CircleNotch size={16} className="animate-spin" /> Checking visibility...</span>
                  ) : (
                    <span className="flex items-center gap-2"><Target size={16} /> Run GEO Check</span>
                  )}
                </Button>
              </div>
            </div>

            {geoResults && (
              <div className="space-y-4 animate-fade-in-up">
                {/* Score Header */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="nx-metric-card rounded-lg" data-testid="geo-llm-score">
                    <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">LLM Visibility Score</p>
                    <p className={`text-4xl font-bold font-mono ${
                      geoResults.llm_visibility_score >= 60 ? 'text-[var(--success)]' : geoResults.llm_visibility_score >= 30 ? 'text-[var(--warning)]' : 'text-[var(--error)]'
                    }`}>{geoResults.llm_visibility_score}%</p>
                  </div>
                  <div className="nx-metric-card rounded-lg">
                    <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Answer Mentions</p>
                    <p className="text-2xl font-bold text-[var(--text-primary)] font-mono">{geoResults.answer_mentions}/{geoResults.prompts_tested}</p>
                  </div>
                  <div className="nx-metric-card rounded-lg">
                    <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Result Appearances</p>
                    <p className="text-2xl font-bold text-[var(--text-primary)] font-mono">{geoResults.result_mentions}/{geoResults.prompts_tested}</p>
                  </div>
                  <div className="nx-metric-card rounded-lg">
                    <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Source</p>
                    <span className="nx-tag nx-tag-green">Live Tavily Data</span>
                  </div>
                </div>

                {/* Per-prompt results */}
                <div className="nx-metric-card rounded-lg" data-testid="geo-prompt-results">
                  <h4 className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-3">Prompt-by-Prompt Results</h4>
                  <div className="space-y-3">
                    {geoResults.visibility_results?.map((v, i) => (
                      <div key={i} className="p-3 rounded border border-[var(--border-light)]">
                        <div className="flex items-center gap-3 mb-2">
                          <p className="text-sm text-[var(--text-primary)] flex-1">"{v.prompt}"</p>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`flex items-center gap-1 text-xs font-mono ${v.in_answer ? 'text-[var(--success)]' : 'text-[var(--text-tertiary)]'}`}>
                              {v.in_answer ? <Check size={12} weight="bold" /> : <X size={12} />}
                              Answer
                            </span>
                            <span className={`flex items-center gap-1 text-xs font-mono ${v.in_results ? 'text-[var(--success)]' : 'text-[var(--text-tertiary)]'}`}>
                              {v.in_results ? <Check size={12} weight="bold" /> : <X size={12} />}
                              Results{v.position ? ` (#${v.position})` : ''}
                            </span>
                          </div>
                        </div>
                        {v.answer_snippet && (
                          <p className="text-xs text-[var(--text-secondary)] leading-relaxed line-clamp-2">{v.answer_snippet}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recommendations */}
                {geoResults.recommendations?.length > 0 && (
                  <div className="nx-metric-card rounded-lg" data-testid="geo-live-recommendations">
                    <h4 className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-3">
                      <ChartLine size={14} className="inline mr-1.5" />
                      Recommendations
                    </h4>
                    <ul className="space-y-2">
                      {geoResults.recommendations.map((r, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-[var(--text-secondary)]">
                          <span className="w-5 h-5 rounded bg-[var(--accent-light)] text-[var(--accent)] text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-mono">
                            {i + 1}
                          </span>
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      <GeoRunConfirmDialog confirm={geoConfirm} running={geoChecking} onCancel={cancelGeoConfirm} onConfirm={runGeoConfirmed} />
    </DashboardLayout>
  );
}
